import { Box, Button, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import React from 'react'
import { emailFrameHtml } from '../../components/TemplateRenderer'
import { ACCENT, RADIUS, SURFACE, TEXT } from '../../theme/tokens'

// An email's file is a .tmpl, as the old app's upload and a download from the
// library both are.
export const HTML_FILE_ACCEPT = '.tmpl'

/**
 * An email whose content is an HTML file, not a builder design: what the
 * file is, the buttons to choose or keep it, and the HTML shown read-only in
 * a frame with no scripts.
 *
 * Two forms use it. Creating from a file ("Upload a .tmpl file", where the
 * library's Upload Template lands): until a file is chosen it asks for one.
 * Editing an email that has no builder design (uploaded as HTML, or made in
 * the old editor): its saved HTML stays unless a file replaces it, and it can
 * be duplicated into the builder.
 */
const EmailHtmlPanel = ({
    isEdit,
    html,
    fileName,
    onChooseFile,
    onKeepSaved,
    onDuplicate,
}) => {
    const chooser = (label) => (
        <Button variant="outlined" component="label">
            {label}
            <input
                hidden
                type="file"
                accept={HTML_FILE_ACCEPT}
                aria-label="Email .tmpl file"
                onChange={onChooseFile}
            />
        </Button>
    )
    let message
    if (!isEdit)
        message = fileName
            ? `The email is made from ${fileName}: its HTML is saved as the email's body, with every field above.`
            : 'Choose the email as a .tmpl file, such as one downloaded from the library. It is saved as the body, with every field above.'
    else
        message = fileName
            ? `The HTML below is from ${fileName}; it replaces the saved HTML when you save.`
            : 'This email has no design the builder can open (it was uploaded as HTML or made in the old editor). Its HTML stays as it is unless you replace it with a file. To change it in the builder, duplicate it into the new editor.'
    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    background: ACCENT.bg08,
                    border: `1px solid ${ACCENT.border30}`,
                    borderRadius: `${RADIUS.md}px`,
                    px: 2,
                    py: 1.25,
                    mb: 2,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{ color: TEXT.secondary, flex: 1, minWidth: 240 }}
                >
                    {message}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {!isEdit &&
                        chooser(
                            fileName
                                ? 'Choose another file'
                                : 'Choose a .tmpl file'
                        )}
                    {isEdit &&
                        (fileName ? (
                            <Button variant="outlined" onClick={onKeepSaved}>
                                Keep the saved HTML
                            </Button>
                        ) : (
                            chooser('Replace HTML')
                        ))}
                    {isEdit && (
                        <Button
                            variant="contained"
                            onClick={onDuplicate}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Duplicate into new editor
                        </Button>
                    )}
                </Box>
            </Box>
            {(isEdit || fileName) && (
                <Box
                    component="iframe"
                    title="Legacy template preview"
                    // No scripts: the HTML is shown, never run.
                    sandbox=""
                    srcDoc={emailFrameHtml(html)}
                    sx={{
                        width: '100%',
                        height: 640,
                        border: `1px solid ${SURFACE.border}`,
                        borderRadius: `${RADIUS.md}px`,
                        background: SURFACE.emailCanvas,
                    }}
                />
            )}
        </>
    )
}

EmailHtmlPanel.propTypes = {
    // Editing a saved email (true) or creating one from a file (false).
    isEdit: PropTypes.bool.isRequired,
    // The HTML shown: the chosen file's, else the saved one.
    html: PropTypes.string.isRequired,
    // The chosen file's name, '' while none is chosen.
    fileName: PropTypes.string.isRequired,
    // The file input's change handler.
    onChooseFile: PropTypes.func.isRequired,
    // Edit: drop the chosen file and keep the saved HTML.
    onKeepSaved: PropTypes.func,
    // Edit: open the HTML in the builder as a new template.
    onDuplicate: PropTypes.func,
}

export default EmailHtmlPanel
